import { CSVImportWizard, FieldToMap } from './base'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import React, { useMemo } from 'react'
import { ContactType, IContact } from '@query'
import { BusinessCountry } from '@query/businesses'
import { CONTACT_TYPE_OPTIONS } from '@constants'
import { Badge } from '@components'
import { createImportJob, createImportJobDryRun, ImportJobType } from '@query/importJob.ts'
import { useBusinessContext, useFeature, useFlag } from '@context'
import { useInvoicingLanguages } from '@root/query/hooks'
import { getOptionLabelByValue } from '@utils'

interface Props {
  onComplete: () => void
}

export const ContactImportWizard: React.FC<Props> = ({ onComplete }) => {
  const { t } = useTranslation()
  const { slug, data: business } = useBusinessContext()
  const einvoicingEnabled = useFeature('einvoicing').enabled
  const isFinnishBusiness = business?.country === BusinessCountry.FI
  const { data: invoicingLanguageOptions = [] } = useInvoicingLanguages()
  const apixXmlBuyerAddressOverrideEnabled = useFlag('apix_xml_buyer_address_override').enabled

  const allFields: FieldToMap<keyof IContact>[] = useMemo(() => {
    const requiredField = yup.string().required(t('validation.required'))
    return [
      {
        name: t('contacts.form.id'),
        info: t('contacts.form.idInfo'),
        csvField: 'ID',
        apiField: 'id',
        required: false,
        isIdentifier: true,
        fieldValidator: yup.string().optional(),
        valueValidator: yup
          .number()
          .nullable()
          .transform((_, value) => {
            return value === '' ? null : Number(value)
          }),
        hiddenByDefault: true
      },
      {
        name: t('contacts.form.customerId'),
        info: t('contacts.form.customerIdInfo'),
        csvField: 'Customer ID',
        apiField: 'customer_id',
        required: false,
        isIdentifier: true,
        fieldValidator: yup.string().optional(),
        valueValidator: yup.string().optional()
      },
      {
        name: t('contacts.form.type'),
        info: t('contacts.form.typeInfo'),
        csvField: 'Contact Type',
        apiField: 'type',
        required: true,
        fieldValidator: requiredField,
        valueValidator: requiredField.test(
          'is-valid-type',
          t('validation.invalid_contact_type'),
          value => {
            return [ContactType.PERSON, ContactType.BUSINESS].includes(ContactType[value])
          }
        ),
        renderExample: value => (
          <Badge>{getOptionLabelByValue(CONTACT_TYPE_OPTIONS, ContactType[value], { t })}</Badge>
        )
      },
      {
        name: t('contacts.form.name'),
        csvField: 'Name',
        apiField: 'name',
        required: true,
        fieldValidator: requiredField,
        valueValidator: yup.string().optional()
      },
      {
        name: t('contacts.form.businessId'),
        info: t('contacts.form.businessIdInfo'),
        csvField: 'Business ID',
        apiField: 'contact_business_id',
        required: false,
        fieldValidator: yup.string().optional(),
        valueValidator: yup.string().optional()
      },
      {
        name: t('contacts.form.phoneNumber'),
        csvField: 'Phone',
        apiField: 'phone_number',
        required: false,
        fieldValidator: yup.string().optional(),
        valueValidator: yup.string().optional()
      },
      {
        name: t('contacts.form.email'),
        csvField: 'Invoicing Email',
        apiField: 'invoicing_email',
        info: '',
        required: false,
        fieldValidator: yup.string().optional(),
        valueValidator: yup.string().optional()
      },
      {
        name: t('contacts.form.einvoiceAddress'),
        info: t('contacts.form.einvoiceAddressInfo'),
        csvField: 'Einvoice Address',
        apiField: 'invoicing_einvoice_address',
        required: false,
        fieldValidator: yup.string().optional(),
        valueValidator: yup.string().optional()
      },
      {
        name: t('contacts.form.einvoiceOperator'),
        info: t('contacts.form.einvoiceOperatorInfo'),
        csvField: 'Einvoice Operator',
        apiField: 'invoicing_einvoice_operator',
        required: false,
        fieldValidator: yup.string().optional(),
        valueValidator: yup.string().optional()
      },
      {
        name: t('contacts.form.taxNumber'),
        info: t('contacts.form.taxNumberInfo'),
        csvField: 'Tax Code',
        apiField: 'invoicing_tax_code',
        required: false,
        fieldValidator: yup.string().optional(),
        valueValidator: yup.string().optional()
      },
      {
        name: t('contacts.form.invoicingStreet'),
        csvField: 'Street',
        apiField: 'invoicing_street',
        required: false,
        fieldValidator: yup.string().optional(),
        valueValidator: yup.string().optional()
      },
      {
        name: t('contacts.form.invoicingCity'),
        csvField: 'City',
        apiField: 'invoicing_city',
        required: false,
        fieldValidator: yup.string().optional(),
        valueValidator: yup.string().optional()
      },
      {
        name: t('contacts.form.invoicingPostalCode'),
        csvField: 'Postal Code',
        apiField: 'invoicing_postal_code',
        required: false,
        fieldValidator: yup.string().optional(),
        valueValidator: yup.string().optional()
      },
      {
        name: t('contacts.form.invoicingCountry'),
        csvField: 'Country',
        apiField: 'invoicing_country',
        required: false,
        fieldValidator: yup.string().optional(),
        valueValidator: yup.string().optional()
      },
      {
        name: t('contacts.form.invoicingLanguage'),
        info: t('contacts.form.invoicingLanguageHint'),
        csvField: 'Invoicing Language',
        apiField: 'invoicing_language',
        required: false,
        fieldValidator: yup.string().optional(),
        valueValidator: yup.string().lowercase().optional(),
        renderExample: value =>
          getOptionLabelByValue(
            invoicingLanguageOptions,
            typeof value === 'string' ? value.toLowerCase() : value
          )
      },
      {
        name: t('contacts.form.apixXmlBuyerAddressOverride'),
        info: t('contacts.form.apixXmlBuyerAddressOverrideInfo'),
        csvField: 'Apix XML Buyer Address Override',
        apiField: 'buyer_postal_address_details_xml_override',
        required: false,
        fieldValidator: yup.string().optional(),
        valueValidator: yup.string().optional()
      },
      {
        name: t('contacts.form.notes'),
        csvField: 'Notes',
        apiField: 'notes',
        required: false,
        fieldValidator: yup.string().optional(),
        valueValidator: yup.string().optional()
      }
    ]
  }, [invoicingLanguageOptions, t])

  const disabledApiFields = new Set<string>()
  if (!einvoicingEnabled || !isFinnishBusiness) {
    disabledApiFields.add('invoicing_einvoice_address')
    disabledApiFields.add('invoicing_einvoice_operator')
  }
  if (!apixXmlBuyerAddressOverrideEnabled || !isFinnishBusiness) {
    disabledApiFields.add('buyer_postal_address_details_xml_override')
  }

  const fields = allFields.filter(f => !disabledApiFields.has(f.apiField as string))

  return (
    <CSVImportWizard
      fields={fields}
      onVerifyData={async data => {
        const { to_be_created: toBeCreated, to_be_updated: toBeUpdated } =
          await createImportJobDryRun({ slug }, { type: ImportJobType.CONTACTS, data })
        return { toBeCreated, toBeUpdated }
      }}
      onImportData={async data => {
        const result = await createImportJob({ slug }, { type: ImportJobType.CONTACTS, data })
        onComplete()
        return result
      }}
    />
  )
}
